import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { get, isArray } from 'lodash';
import { Tooltip } from '@mui/material';
import { GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { getAllUnits, removeUnit } from './_apiUnits';
import UnitForm from './UnitForm';
import OverflowedDialog from 'components/Dialog/OverflowedDialog';
import Header from 'components/Header/Header';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { useWithTitle } from 'utils/hooks/useWithTitle';
import { useEntityRemove } from 'utils/hooks/useEntityRemove';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useAppDispatch } from 'store/hooks';
import { storeConfirmationDataToStore } from 'store/reducers/appReducer';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';

const muiGridKey = 'unitsMUI';

const Units: React.FC = () => {
  const { t } = useTranslation('Units');
  useWithTitle();
  const dispatch = useAppDispatch();
  const { confirmationData } = useAppInfo();
  const { toggleLoader } = useAppGlobals();

  const [dialogState, setDialogState] = useState<boolean>(false);
  const [unitId, setUnitId] = useState<number>(0);
  const [units, setUnits] = useState<any[]>([]);

  const onEntityEdit = async (id: number) => {
    setUnitId(id);
    toggleDialog(id > 0 ? true : false);
  };

  const loadEntity = async () => {
    toggleLoader();
    setUnitId(0);

    try {
      const units = await getAllUnits();
      if (units && isArray(units)) {
        setUnits(units);
      }
    } catch (e) {
      console.debug(e);
    }
    toggleLoader(false);
  };

  const { onEntityRemove } = useEntityRemove(removeUnit, t, loadEntity);

  const clearAction = () => {
    dispatch(storeConfirmationDataToStore(null));
  };

  const confirmAction = async () => {
    await onEntityRemove(get(confirmationData, 'id'));
    clearAction();
  };

  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);

  const columns = reorderColumnsByGridSettings(
    injectColumnWidthsIntoColumns([
      {
        field: 'actions',
        headerName: t('Grid:actions'),
        type: 'actions',
        hideable: false,
        width: 120,
        renderCell: ({ row }: GridRenderCellParams) => {
          return (
            <>
              <GridActionsCellItem
                icon={
                  <Tooltip title={t('Grid:edit')}>
                    <EditIcon />
                  </Tooltip>
                }
                label={t('Grid:edit')}
                onClick={() => onEntityEdit(get(row, 'id'))}
                data-pw={`unit_edit_${row.code}`}
              />
              <GridActionsCellItem
                icon={
                  <Tooltip title={t('Grid:delete')}>
                    <DeleteIcon />
                  </Tooltip>
                }
                label={t('Grid:delete')}
                onClick={() => {
                  dispatch(storeConfirmationDataToStore({ id: get(row, 'id') }));
                }}
                disabled={
                  get(row, 'state', null) === 'NEW' ||
                  !get(row, 'canDelete', true) ||
                  get(row, 'system', false)
                }
                data-pw={`unit_delete_${row.code}`}
              />
            </>
          );
        },
      },
      { field: 'code', headerName: t('code') },
      { field: 'name', headerName: t('name') },
    ]),
  );

  useEffect(() => {
    loadEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDialog = (state: boolean = false) => {
    setDialogState(state);
  };

  const addItemInDialog = () => {
    setUnitId(0);
    toggleDialog(true);
  };

  const handleClose = () => {
    toggleDialog(false);
    setUnitId(0);
  };

  const id = unitId ? unitId : dialogState ? 'create' : null;

  const renderedDialog = (
    <OverflowedDialog
      open={dialogState}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
      fullWidth={true}
    >
      <UnitForm toggleDialog={toggleDialog} id={id} loadEntity={loadEntity} />
    </OverflowedDialog>
  );

  return (
    <>
      <Header
        title=""
        addItemInDialog={addItemInDialog}
        toggleDialogText={t('add')}
        topMargin={true}
        dataPw="units-header-title"
      />
      <MuiGrid
        rows={units}
        columns={columns}
        initialSortMode={[{ field: 'name', sort: 'asc' }]}
        gridKey={muiGridKey}
      />
      {renderedDialog}

      {confirmationData && confirmationData.id && !confirmationData.editDialog && (
        <ConfirmationDialog
          title={confirmationData.title ? t(confirmationData.title) : t('Grid:confirmDelete')}
          open={true}
          aria-labelledby="form-dialog-title"
          fullWidth={true}
          cancelAction={clearAction}
          confirmAction={confirmAction}
        />
      )}
    </>
  );
};

export default Units;
